import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { plataformaApi, type NovoMandato } from '@/lib/api/plataforma';
import { useSessaoDeOperador } from './sessaoDeOperador';
import { toast } from '@/lib/stores/toastStore';

const CARGOS = [
  ['vereador', 'Vereador'],
  ['prefeito', 'Prefeito'],
  ['deputado_estadual', 'Deputado estadual'],
  ['deputado_federal', 'Deputado federal'],
  ['governador', 'Governador'],
  ['senador', 'Senador'],
  ['presidente', 'Presidente'],
] as const;

const VAZIO: NovoMandato = {
  nome: '',
  cargo: 'vereador',
  cidade: '',
  uf: 'CE',
  plano: 'starter',
  modulos: ['gab'],
  nome_titular: '',
  email_titular: '',
  senha_titular: '',
};

/**
 * Listagem e cadastro de mandatos.
 *
 * A lista vem da função `mandatos_para_operador`, que devolve só o que esta
 * tela mostra -- nunca senha, nunca dado de cidadão. O operador cadastra
 * gabinetes; ele não entra neles.
 */
export function MandatosPage() {
  const navigate = useNavigate();
  const token = useSessaoDeOperador((s) => s.token);
  const operador = useSessaoDeOperador((s) => s.operador);
  const sair = useSessaoDeOperador((s) => s.sair);
  const qc = useQueryClient();
  const [form, setForm] = useState<NovoMandato | null>(null);

  const { data: mandatos = [], isLoading } = useQuery({
    queryKey: ['plataforma', 'mandatos'],
    queryFn: () => plataformaApi.listarMandatos(token!),
    enabled: !!token,
  });

  const criar = useMutation({
    mutationFn: (dados: NovoMandato) => plataformaApi.criarMandato(token!, dados),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plataforma', 'mandatos'] });
      setForm(null);
      toast('Mandato criado.');
    },
    onError: (e: any) => toast(e?.response?.data?.message ?? 'Não foi possível criar o mandato.'),
  });

  if (!token) {
    navigate('/plataforma');
    return null;
  }

  const editar = (campo: keyof NovoMandato, valor: string) =>
    setForm((f) => (f ? { ...f, [campo]: valor } : f));

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Mandatos</h1>
          <p className="sub">
            {operador?.nome} · {mandatos.length} mandato(s) na plataforma
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn pri" onClick={() => setForm(VAZIO)}>
            + Novo mandato
          </button>
          <button className="btn sm" onClick={() => { sair(); navigate('/plataforma'); }}>
            sair
          </button>
        </div>
      </div>

      <div className="card flat tw">
        <table>
          <thead>
            <tr>
              <th>Mandato</th>
              <th>Cargo</th>
              <th>Município</th>
              <th>Plano</th>
              <th>Módulos</th>
              <th>Usuários</th>
            </tr>
          </thead>
          <tbody>
            {mandatos.map((m) => (
              <tr key={m.id}>
                <td>
                  <b>{m.nome_parlamentar || m.nome}</b>
                  {!!m.partido && <div className="mono">{m.partido}</div>}
                </td>
                <td>{m.cargo}</td>
                <td>{m.cidade} · {m.uf}</td>
                <td>{m.plano}</td>
                <td>{(m.modulos ?? []).join(', ')}</td>
                <td>{m.usuarios}</td>
              </tr>
            ))}
            {mandatos.length === 0 && (
              <tr>
                <td colSpan={6} className="empty">
                  {isLoading ? 'Carregando…' : 'Nenhum mandato cadastrado ainda.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>Novo mandato</h2>
          <div className="g2">
            <label className="field">Nome do mandato
              <input className="in" value={form.nome} onChange={(e) => editar('nome', e.target.value)} />
            </label>
            <label className="field">Cargo
              <select className="sel" value={form.cargo} onChange={(e) => editar('cargo', e.target.value)}>
                {CARGOS.map(([v, r]) => <option key={v} value={v}>{r}</option>)}
              </select>
            </label>
            <label className="field">Município
              <input className="in" value={form.cidade} onChange={(e) => editar('cidade', e.target.value)} />
            </label>
            <label className="field">UF
              <input className="in" maxLength={2} value={form.uf} onChange={(e) => editar('uf', e.target.value.toUpperCase())} />
            </label>
            <label className="field">Nome do titular
              <input className="in" value={form.nome_titular} onChange={(e) => editar('nome_titular', e.target.value)} />
            </label>
            <label className="field">E-mail do titular
              <input className="in" type="email" value={form.email_titular} onChange={(e) => editar('email_titular', e.target.value)} />
            </label>
            <label className="field">Senha inicial do titular
              <input className="in" type="password" value={form.senha_titular} onChange={(e) => editar('senha_titular', e.target.value)} />
              <small>Mínimo de 12 caracteres. É a conta de maior privilégio do gabinete.</small>
            </label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn pri" disabled={criar.isPending} onClick={() => criar.mutate(form)}>
              {criar.isPending ? 'Criando…' : 'Criar mandato'}
            </button>
            <button className="btn sm" onClick={() => setForm(null)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
